import {InputRightElement, ModalHeader} from '@chakra-ui/react';
import React, {memo, useEffect, useState} from 'react';
import {RiArrowDropDownLine} from 'react-icons/ri';
import {
  AppDatePicker,
  AppModal,
  Col,
  UiButton,
  UiCenter,
  UiIcon,
  UiInput,
  UiInputGroup,
  UiSpinner,
  UiText,
} from '~/components/elements';
import {APPOINTMENT_BOOKING_TYPE} from '~/constants/appointment-bookings';
import {formatLongDate} from '~/utils/helpers';

interface Props {
  variant?: string;
  headerTitle?: string;
  maxDate?: Date;
  minDate?: Date;
  placeholder?: string;
  defaultValue?: Date;
  onChange?: (value?: Date) => void;
  filterDate?: (date: Date) => boolean;
  loading?: boolean;
  showNoSlotWantToBook?: boolean;
  isPlanBBooking?: boolean;
}

export const UiInputDatePicker = memo(function UiInputDatePicker({
  variant,
  headerTitle,
  placeholder,
  maxDate,
  minDate,
  defaultValue,
  onChange,
  filterDate,
  loading,
  showNoSlotWantToBook = false,
  isPlanBBooking = false,
  ...rest
}: Props) {
  const [isShowModal, setIsShowModal] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultValue);

  const handleModalVisible = () => {
    setIsShowModal(!isShowModal);
    setSelectedValue(defaultValue);
  };

  const onClickConfirm = () => {
    if (onChange) {
      onChange(selectedValue);
    }
    handleModalVisible();
  };

  const onClickNoSlot = () => {
    handleModalVisible();
    let postMessage: {
      func: string;
      message: string;
      type?: number;
    } = {
      func: 'triggerChat',
      message: 'Trigger chat from iframe.',
    };
    if (isPlanBBooking) {
      postMessage = {
        ...postMessage,
        type: +APPOINTMENT_BOOKING_TYPE.THIRTY_MINS,
      };
    }
    if (window.parent) {
      window.parent.postMessage(postMessage, '*');
    }
  };

  useEffect(() => {
    setSelectedValue(defaultValue);
  }, [defaultValue]);

  const renderContent = () => {
    if (loading)
      return (
        <Col height="250px" width="100%" justifyContent="center" alignItems="center">
          <UiSpinner thickness="4px" speed="0.65s" emptyColor="lightestGrey" size="xl" />
        </Col>
      );

    return (
      <Col>
        <AppDatePicker
          startDate={undefined}
          inline
          modalContentProps={{marginTop: 'auto', marginBottom: 10}}
          maxDate={maxDate}
          minDate={minDate}
          onChange={setSelectedValue}
          selected={selectedValue}
          filterDate={filterDate}
        />
        {showNoSlotWantToBook && (
          <Col alignItems="center" justifyContent="center">
            <UiText
              textAlign="center"
              fontWeight="600"
              color="graySecondary"
              my={1}
              onClick={onClickNoSlot}
              cursor="pointer"
            >
              I don&apos;t see a slot that I want
            </UiText>
          </Col>
        )}

        <UiButton disabled={!selectedValue} title="Confirm" mt="20px" mx="15%" onClick={onClickConfirm} />
      </Col>
    );
  };

  return (
    <>
      <AppModal
        isOpen={isShowModal}
        onClose={handleModalVisible}
        isBottom
        closeButtonSize="lg"
        actions={null}
        header={
          <ModalHeader fontSize="lg" textAlign="center">
            {headerTitle}
          </ModalHeader>
        }
        autoFocus={false}
        loading={loading}
      >
        <UiCenter mt={3}>{renderContent()}</UiCenter>
      </AppModal>
      <UiInputGroup>
        <UiInput
          borderRadius={8}
          size="lg"
          fontSize="md"
          placeholder={placeholder}
          isReadOnly
          onClick={() => setIsShowModal(true)}
          bg="white"
          defaultValue={formatLongDate(defaultValue)}
          {...rest}
        />
        <InputRightElement pointerEvents="none" mr={1} mt={2}>
          <UiIcon as={RiArrowDropDownLine} boxSize={8} />
        </InputRightElement>
      </UiInputGroup>
    </>
  );
});
